import { gatewayShopifyGraphQL, getEnvShopifyGraphQLGatewayConfig } from './shopify-graphql-gateway';
import {
    flattenEdges,
    GraphQLResponse,
    OperationVariables,
    SHOPIFY_API_VERSION,
    ShopifyGraphQLError,
    ShopifyGraphQLOptions,
} from './shopify-graphql.utils';

export {
    extractNodes,
    flattenEdges,
    gatewayUrlWithApiVersion,
    SHOPIFY_API_VERSION,
    ShopifyGraphQLError,
    type Connection,
    type Edge,
    type GraphQLResponse,
    type OperationVariables,
    type ShopifyGraphQLOptions,
} from './shopify-graphql.utils';

const shopifyGraphQLUrl = (version = SHOPIFY_API_VERSION) => `shopify:admin/api/${version}/graphql.json`;

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;

function isRateLimitError(response: Response, json?: GraphQLResponse<unknown>): boolean {
    if (response.status === 429) {
        return true;
    }

    return (
        json?.errors?.some(
            (error) =>
                error.message.toLowerCase().includes('throttled') || error.message.toLowerCase().includes('rate limit')
        ) ?? false
    );
}

function getRetryDelay(attempt: number, response: Response): number {
    const retryAfter = response.headers.get('Retry-After');
    if (retryAfter) {
        const seconds = parseInt(retryAfter, 10);
        if (!isNaN(seconds)) {
            return seconds * 1000;
        }
    }

    // Exponential backoff with jitter
    const exponentialDelay = BASE_DELAY_MS * Math.pow(2, attempt);
    const jitter = Math.random() * 500;
    return exponentialDelay + jitter;
}

async function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function shopifyGraphQL<TData = unknown, TVariables extends OperationVariables = OperationVariables>(
    query: string,
    variables?: TVariables,
    options?: ShopifyGraphQLOptions
): Promise<TData> {
    // With VITE_APP_SHOPIFY_GRAPHQL_MODE=proxy every caller (hooks, thunks, services) is
    // routed through the backend gateway instead of App Bridge.
    const gateway = getEnvShopifyGraphQLGatewayConfig();
    if (gateway) {
        return gatewayShopifyGraphQL<TData, TVariables>(gateway, query, variables, options);
    }

    let lastError: ShopifyGraphQLError | null = null;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        const response = await fetch(shopifyGraphQLUrl(options?.version), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ query, variables }),
        });

        // Handle non-OK responses
        if (!response.ok) {
            if (response.status === 429 && attempt < MAX_RETRIES) {
                const delay = getRetryDelay(attempt, response);
                console.warn(`Shopify rate limit hit, retrying in ${delay}ms (attempt ${attempt + 1}/${MAX_RETRIES})`);
                await sleep(delay);
                continue;
            }
            throw new ShopifyGraphQLError(
                `Shopify GraphQL HTTP error: ${response.status}`,
                undefined,
                response.status === 429
            );
        }

        const json: GraphQLResponse<TData> = await response.json();

        // Check for rate limit errors in GraphQL response
        if (isRateLimitError(response, json) && attempt < MAX_RETRIES) {
            const delay = getRetryDelay(attempt, response);
            console.warn(`Shopify rate limit hit, retrying in ${delay}ms (attempt ${attempt + 1}/${MAX_RETRIES})`);
            await sleep(delay);
            lastError = new ShopifyGraphQLError(json.errors?.[0]?.message || 'Rate limited', json.errors, true);
            continue;
        }

        if (json.errors?.length) {
            throw new ShopifyGraphQLError(json.errors[0].message, json.errors);
        }

        return flattenEdges(json.data) as TData;
    }

    throw lastError ?? new ShopifyGraphQLError('Max retries exceeded due to rate limiting', undefined, true);
}
